import React, { useState, useEffect, useRef } from 'react';
import AppFooter from '../components/AppFooter';
import '../styles/LoginPage.css';

const URL_STORAGE_KEY = 'orcanos_saved_url';       // legacy single-account key (migrated)
const ACCOUNTS_KEY = 'orcanos_accounts';           // list of { url, username, lastUsed }
const CONTACT_URL = 'https://www.orcanos.com/contact-us';

function extractAccount(url) {
  try {
    const path = new URL(url).pathname.replace(/\/$/, '');
    const parts = path.split('/').filter(Boolean);
    if (parts.length > 0) {
      const seg = parts[parts.length - 1];
      return seg.charAt(0).toUpperCase() + seg.slice(1);
    }
  } catch {}
  return url;
}

// Orcanos hosts its own password reset per tenant, under the account URL the user typed:
//   https://app.orcanos.com/acme  ->  https://app.orcanos.com/acme/web/account/forgotpassword
// Returns '' for anything that isn't a usable http(s) URL, so the link is simply not offered.
function forgotPasswordUrl(url) {
  try {
    const u = new URL((url || '').trim());
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
    // origin + pathname only — a stray ?query/#hash on the typed URL must not ride along.
    const base = `${u.origin}${u.pathname}`.replace(/\/+$/, '');
    return `${base}/web/account/forgotpassword`;
  } catch {
    return '';
  }
}

function accountKey(acc) {
  return `${(acc.url || '').toLowerCase()}|${(acc.username || '').toLowerCase()}`;
}

function loadAccounts() {
  let accounts = [];
  try {
    const raw = localStorage.getItem(ACCOUNTS_KEY);
    if (raw) accounts = JSON.parse(raw);
  } catch {}
  if (!Array.isArray(accounts)) accounts = [];

  // Migrate legacy single saved URL (no username stored back then)
  if (accounts.length === 0) {
    const legacy = localStorage.getItem(URL_STORAGE_KEY);
    if (legacy) accounts = [{ url: legacy, username: '', lastUsed: 0 }];
  }

  return accounts.sort((a, b) => (b.lastUsed || 0) - (a.lastUsed || 0));
}

function saveAccounts(accounts) {
  try {
    localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
  } catch {}
}

/**
 * Add or update one saved account, reading and writing localStorage DIRECTLY.
 *
 * ⚠️ Not `setAccounts` + `saveAccounts`: the caller may be about to navigate to
 * another region, and a React state update that never renders would be lost. This
 * writes first and hands back the new list so the caller can update state too when
 * it is staying on the page.
 *
 * Merge rule: an entry is the same account when the URL matches. A hand-off happens
 * before the credentials are even enabled, so it saves with an EMPTY username — it
 * must not create a second, nameless row beside the one the user signed in with, and
 * it must not wipe the username off it either. So a blank username adopts whatever
 * is already stored; a real one wins.
 */
function upsertAccount(entry) {
  const accounts = loadAccounts();
  const sameUrl = (a) => (a.url || '').toLowerCase() === (entry.url || '').toLowerCase();
  const existing = accounts.find((a) => sameUrl(a) && (!entry.username || !a.username ||
    (a.username || '').toLowerCase() === (entry.username || '').toLowerCase()));
  const merged = {
    ...(existing || {}),
    ...entry,
    username: entry.username || (existing && existing.username) || '',
    lastUsed: entry.lastUsed || Date.now()
  };
  const next = [merged, ...accounts.filter((a) => a !== existing)];
  saveAccounts(next);
  return next;
}

/**
 * Renders a gate message with a Contact Us link to the Orcanos contact page, so a blocked
 * user can reach out in one click. Links the phrase "contact us" in the server message
 * (e.g. "Please contact us to open an account"); falls back to replacing a legacy sales
 * address, or appending a "Contact us" link when neither is present.
 */
function GateMessage({ text, email }) {
  const link = (label) => (
    <a className="gate-contact" href={CONTACT_URL} target="_blank" rel="noopener noreferrer">{label}</a>
  );

  // Preferred: link the words "contact us" wherever they appear (preserving original casing).
  const phrase = /contact us/i.exec(text);
  if (phrase) {
    const i = phrase.index;
    return <>{text.slice(0, i)}{link(text.slice(i, i + phrase[0].length))}{text.slice(i + phrase[0].length)}</>;
  }

  // Legacy: a message that still names the sales address -> swap it for a Contact us link.
  const addr = email || 'sales@orcanos.com';
  const i = text.indexOf(addr);
  if (i !== -1) {
    return <>{text.slice(0, i)}{link('Contact us')}{text.slice(i + addr.length)}</>;
  }

  return <>{text}{' '}{link('Contact us')}</>;
}

/**
 * What a first-time visitor finds in the URL field.
 *
 * `[YOUR ACCOUNT]` is a BLANK TO FILL IN, not a tenant. This used to ship a real
 * account name (`orca60`), which showed every new customer somebody else's account
 * — and, once that tenant moved to the EU, tried to hand them off to Frankfurt on
 * the strength of a value nobody had entered. A placeholder is never checked against
 * the allowlist and never signed in with: the form waits for a real account name.
 */
const ACCOUNT_PLACEHOLDER = '[YOUR ACCOUNT]';
const DEFAULT_URL = `https://app.orcanos.com/${ACCOUNT_PLACEHOLDER}`;
const isPlaceholderUrl = (u) => !u || !u.trim() || u.includes(ACCOUNT_PLACEHOLDER);

/**
 * Which region this server serves, in plain words.
 *
 * Only rendered once the server has actually said — never guessed from the
 * hostname, which would be a second source of truth for the one fact this whole
 * feature exists to keep straight.
 */
const REGION_NAMES = { us: 'United States', eu: 'European Union' };

/** The same fact in two words, for the badge on a saved-account row. */
const REGION_SHORT = { us: 'US', eu: 'EU' };

function RegionNote({ region }) {
  const name = REGION_NAMES[region];
  if (!name) return null;
  return (
    <p className="region-note" title="Where this account's data is stored.">
      {name} region
    </p>
  );
}

/** Eye icon for the show/hide password toggle; `off` draws the struck-through (hidden) state. */
function EyeIcon({ off }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
      {off && <line x1="3" y1="21" x2="21" y2="3" />}
    </svg>
  );
}


/**
 * One URL for everybody, routed by region.
 *
 * Customers keep the single address they have always used. When the account they
 * type belongs to another region's deployment, the login screen hands them over
 * automatically instead of asking them to know which address is theirs — the
 * region is an implementation detail of where we keep their data, not something
 * a customer should have to remember.
 *
 * ⚠️ THE HAND-OFF CARRIES NO CREDENTIALS. It happens on the Orcanos URL alone,
 * before the password fields are even enabled, because a password arriving at
 * the wrong region would be a cross-border transfer of personal data in its own
 * right (Critical Note #55). The only thing in the query string is the Orcanos
 * URL the user already typed, so they do not have to type it again.
 *
 * `rr=1` is a LOOP GUARD, and it is not theoretical: the two instances hold
 * separate copies of the region directory, and if they ever disagree — a
 * half-finished move, a directory write that failed on one side — an automatic
 * redirect would bounce the browser between two servers forever, each certain
 * the customer belongs to the other. Arriving with the flag set means "you have
 * already been sent once": show the panel with the link and let a person decide.
 */
const REDIRECTED_PARAM = 'rr';
const CARRIED_URL_PARAM = 'u';

/**
 * Remove the carried URL from the address bar, keeping everything else.
 *
 * `replaceState` so it leaves no history entry — the user did not navigate, we
 * are tidying up after ourselves. Wrapped because a browser that refuses it
 * (rare, but `file://` and some embedded webviews do) must not take the login
 * screen down with it; the worst case without it is the old behaviour.
 */
function stripCarriedUrl() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(CARRIED_URL_PARAM)) return;
    url.searchParams.delete(CARRIED_URL_PARAM);
    window.history.replaceState({}, '', url.toString());
  } catch {}
}

function handOffTo(regionUrl, orcanosUrl) {
  const target = new URL(regionUrl);
  // Land on /login directly. `REGION_URL_*` is a bare origin, so without this the
  // hand-off arrives at `/`, which is not a route while signed out and is caught
  // by the catch-all. That redirect used to drop the query string — belt and
  // braces now that it preserves it, but arriving at the right route in the first
  // place is one fewer thing that has to keep working.
  target.pathname = '/login';
  target.searchParams.set(REDIRECTED_PARAM, '1');
  if (orcanosUrl) target.searchParams.set(CARRIED_URL_PARAM, orcanosUrl);
  // `replace`, not `assign`: Back must return to wherever the user came from,
  // not to the address that just bounced them.
  window.location.replace(target.toString());
}

function LoginPage({ onLogin, version, notice }) {
  const [accounts, setAccounts] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);   // key of active saved account, or null = new-account form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [orcanos_url, setOrcanos_url] = useState(DEFAULT_URL);
  // A saved account hides the URL behind a summary row; clicking it opens the real input so the
  // URL can be read in full, copied, or corrected without deleting and re-adding the account.
  const [urlOpen, setUrlOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Account allowlist gate. status: 'idle' | 'checking' | 'ok' | 'unknown' | 'denied'
  // Credentials stay disabled until the account is known-good, so a blocked user is told
  // before typing a password rather than after.
  const [gate, setGate] = useState({ status: 'idle', message: '', email: 'sales@orcanos.com' });
  // Set while the browser is on its way to another region, so the form is not
  // briefly interactive during the navigation.
  const [handingOff, setHandingOff] = useState(false);
  // Read once: these drive whether an automatic hand-off is allowed at all.
  const params = useRef(new URLSearchParams(window.location.search));
  const alreadyRedirected = params.current.get(REDIRECTED_PARAM) === '1';
  /**
   * Has the person edited the Orcanos URL since arriving?
   *
   * The loop guard (`rr=1`) exists to stop two servers bouncing a browser
   * between them WITHOUT anybody asking. A URL somebody just typed is not that:
   * refusing to forward it left them stuck on the region they were last sent to,
   * being told their other account is "hosted somewhere else" with no way to go
   * there — which is the exact dead end this whole feature removes. So an edit
   * re-arms the hand-off, and the guard keeps applying to the automatic case.
   */
  const [urlEdited, setUrlEdited] = useState(false);
  // Data residency: this account's data lives in another region's deployment, so signing in
  // here cannot work and must not be attempted. Rendered as its own panel rather than through
  // GateMessage — "contact us" is the wrong call to action when the user just needs a link,
  // and the credentials stay disabled because a password sent to the wrong region would be a
  // cross-border transfer of personal data even though this server refuses it.
  const wrongRegion = gate.status === 'wrong_region';
  const gateBlocked = gate.status === 'unknown' || gate.status === 'denied';
  // Still holding the blank — the credentials stay locked, but this is a prompt, not a refusal.
  const awaitingAccount = isPlaceholderUrl(orcanos_url);
  const credsEnabled = gate.status === 'ok' && !handingOff;
  // No account name yet ⇒ no reset link to offer: it would point at `/[YOUR ACCOUNT]/…`.
  const forgotUrl = isPlaceholderUrl(orcanos_url) ? '' : forgotPasswordUrl(orcanos_url);

  /**
   * `allowHandOff` — may this check forward the browser to another region?
   *
   * Only for a URL the PERSON stands behind: one they typed, or a saved account
   * they signed into before. Never for a value nobody entered — see
   * ACCOUNT_PLACEHOLDER, which the form now opens with instead of a real tenant.
   */
  /**
   * Save an account that lives in ANOTHER region, on this side of the hand-off.
   *
   * `region`/`regionUrl` are stored so the row can say where it goes before anything
   * is checked — but they are a LABEL, never an authority: selecting the row still
   * asks the server, which remains the only thing that decides where an account lives.
   */
  const rememberRegionAccount = (url, region, regionUrl) => {
    const next = upsertAccount({ url, username, region: region || '', regionUrl: regionUrl || '' });
    setAccounts(next);
  };

  const checkAccount = async (url, { allowHandOff = true } = {}) => {
    // `[YOUR ACCOUNT]` is not an account: asking the allowlist about it can only
    // answer "denied", which reads as a refusal of a form the user has not filled in yet.
    if (isPlaceholderUrl(url)) {
      setGate({ status: 'idle', message: '', email: 'sales@orcanos.com' });
      return;
    }
    setGate((g) => ({ ...g, status: 'checking', message: '' }));
    try {
      const resp = await fetch('/api/auth/check-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orcanos_url: url })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error('check failed');
      // Wrong region, and we know where they belong: hand over now, before the
      // password fields unlock. Skipped when this page IS the redirect target
      // (see REDIRECTED_PARAM) — that means the two regions disagree, and
      // bouncing again would loop.
      if (allowHandOff && data.wrong_region && data.region_url && (!alreadyRedirected || urlEdited)) {
        // Remember it HERE, on the region being left. The saved list lives in
        // localStorage, which is per-origin: an account signed into in Frankfurt is
        // saved in Frankfurt and is invisible on the US address, so the whole point
        // of a saved account — not typing the URL again — was lost for exactly the
        // people who have accounts in two regions. Saving at the hand-off puts the
        // row on BOTH sides, and selecting it here simply hands over again.
        rememberRegionAccount(url, data.region, data.region_url);
        setHandingOff(true);
        handOffTo(data.region_url, url);
        return;
      }

      // Wrong region but not forwarded (the loop guard, or no address known): the
      // account is still real and still theirs, so it belongs in the saved list.
      if (data.wrong_region) rememberRegionAccount(url, data.region, data.region_url);

      setGate({
        // 'off' (no allowlist configured on the server) behaves as ok — the gate is disabled.
        status: data.status === 'off' ? 'ok' : data.status,
        message: data.message || '',
        email: data.contact_email || 'sales@orcanos.com',
        // Where to go when this account belongs to another region. May be '' — the server
        // knows the account is elsewhere but was not told that region's address. The user is
        // still told the truth; they just have to be given the link by a human.
        regionUrl: data.region_url || '',
        region: data.region || '',
        // Which region THIS server is. Shown on the form so a person who was
        // forwarded here can see where they landed — being moved to another
        // address with nothing saying why is the confusing part, not the move.
        selfRegion: data.self_region || ''
      });
    } catch {
      // Never strand the user on a network blip: let them try to sign in — /login re-checks
      // and will return the same message if the account really is blocked.
      setGate({ status: 'ok', message: '', email: 'sales@orcanos.com' });
    }
  };

  useEffect(() => {
    const accts = loadAccounts();
    setAccounts(accts);

    // Arrived from the other region's hand-off: the URL the user already typed
    // travels in the query string so they do not type it twice. It wins over a
    // saved account, which would otherwise silently switch them back to whatever
    // they last signed in to on this device.
    const carried = params.current.get(CARRIED_URL_PARAM);
    if (carried) {
      setOrcanos_url(carried);
      setSelectedKey(null);
      checkAccount(carried, { allowHandOff: false });
      // ⚠️ CONSUME IT. The parameter has done its one job — prefilling the field
      // so the user does not type the URL twice — and leaving it in the address
      // bar makes it a permanent instruction instead of a hand-off. Any later
      // re-mount of this page re-read it and overwrote whatever the user had
      // since typed, so the field "popped back" to the account they arrived
      // with and there was no way to change it.
      //
      // `rr` deliberately STAYS: it is the loop guard, and it is about this page
      // load, not about one field.
      stripCarriedUrl();
      return;
    }

    if (accts.length > 0) {
      selectAccount(accts[0], { handOff: false });
    }
    // No saved account ⇒ the field holds the placeholder, which is nobody's account:
    // nothing to check, and one fewer round trip before the form is usable.
  }, []);

  useEffect(() => () => { if (copyTimer.current) clearTimeout(copyTimer.current); }, []);

  /**
   * `handOff` — may merely selecting this account move the browser to another region?
   *
   * Yes when the person CLICKED the row: that is them asking for that account, and
   * being taken to it is the whole point of the row being there.
   *
   * No on the automatic first-load selection. The most recently used account can be
   * one in another region, and forwarding on page load alone would mean opening the
   * US address and being thrown to Frankfurt without touching anything. The row is
   * shown with its region badge instead, and one click goes.
   */
  const selectAccount = (acc, { handOff = true } = {}) => {
    setSelectedKey(accountKey(acc));
    setOrcanos_url(acc.url);
    setUsername(acc.username || '');
    setPassword('');
    setError('');
    setUrlOpen(false);       // each account starts collapsed to its summary row
    // A saved account can be revoked, or moved to another region, between visits.
    checkAccount(acc.url, { allowHandOff: handOff });
  };

  const handleAddAccount = () => {
    setSelectedKey(null);
    setOrcanos_url(DEFAULT_URL);
    setUsername('');
    setPassword('');
    setError('');
    setUrlOpen(false);       // no saved account selected ⇒ the input renders anyway
    setGate({ status: 'idle', message: '', email: gate.email });
  };

  // Editing a saved account's URL makes it a different account, so the picker must stop
  // claiming it — the saved entry is left untouched and a successful sign-in adds a new one.
  const handleUrlChange = (value) => {
    setOrcanos_url(value);
    setUrlEdited(true);
    if (selectedKey) setSelectedKey(null);
    // A stale verdict on a URL that's being retyped is worse than none.
    setGate({ status: 'idle', message: '', email: gate.email });
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(orcanos_url);
    } catch {
      // Clipboard API needs a secure context; fall back to selecting the text to copy by hand.
      const el = document.getElementById('orcanos_url');
      if (el) { el.focus(); el.select(); }
      return;
    }
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1500);
  };

  const handleDeleteAccount = (acc, e) => {
    e.stopPropagation();
    const key = accountKey(acc);
    const next = accounts.filter((a) => accountKey(a) !== key);
    setAccounts(next);
    saveAccounts(next);
    if (key === selectedKey) {
      if (next.length > 0) {
        selectAccount(next[0]);
      } else {
        handleAddAccount();
      }
    }
  };

  const rememberAccount = () => {
    // `regionUrl: ''` on purpose: the account just signed in HERE, so any address
    // stored from an earlier hand-off is out of date and must not outlive it.
    setAccounts(upsertAccount({
      url: orcanos_url,
      username,
      region: gate.selfRegion || '',
      regionUrl: '',
      lastUsed: Date.now()
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, orcanos_url })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 409) {
          // Wrong region. Reachable even though the pre-login check blocks the button: the
          // check can be skipped, raced, or the directory can change between the two calls.
          // The password is cleared because it was just sent to the wrong region — it will
          // have to be typed again at the right address, which is the honest outcome.
          const d = data.detail || {};
          // Same hand-off as the pre-login check. Reachable when the directory
          // changed between the two calls, or the check was skipped entirely.
          if (d.region_url && (!alreadyRedirected || urlEdited)) {
            rememberRegionAccount(orcanos_url, d.region, d.region_url);
            setPassword('');
            setHandingOff(true);
            handOffTo(d.region_url, orcanos_url);
            return;
          }
          rememberRegionAccount(orcanos_url, d.region, d.region_url);
          setGate({
            status: 'wrong_region',
            message: d.message || 'This account is hosted in another region.',
            email: 'sales@orcanos.com',
            regionUrl: d.region_url || '',
            region: d.region || ''
          });
          setPassword('');
        } else if (response.status === 403) {
          // The account gate — the server re-checks on every login, so this fires even if the
          // on-blur check was skipped, raced, or the allowlist changed since the page loaded.
          const msg = data.detail || 'Access is not allowed. Please contact us.';
          setGate({
            status: /doesn't exist|does not exist/i.test(msg) ? 'unknown' : 'denied',
            message: msg,
            email: 'sales@orcanos.com'
          });
          setPassword('');
        } else if (response.status === 401) {
          setError(data.detail || 'Invalid credentials');
        } else {
          setError(`Server error (${response.status}): ${data.detail || 'Please check backend logs'}`);
        }
        setLoading(false);
        return;
      }

      if (data.IsSuccess) {
        localStorage.setItem(URL_STORAGE_KEY, orcanos_url);
        rememberAccount();
        onLogin();
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const showAccountList = accounts.length > 0;

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>Orcanos</h1>
        <h2>Traceability Matrix</h2>

        <form onSubmit={handleSubmit}>
          {showAccountList && (
            <div className="account-picker">
              <span className="account-picker-label">Saved accounts</span>
              <ul className="account-list">
                {accounts.map((acc) => {
                  const key = accountKey(acc);
                  const active = key === selectedKey;
                  return (
                    <li
                      key={key}
                      className={`account-item${active ? ' active' : ''}`}
                      onClick={() => !loading && selectAccount(acc)}
                    >
                      <span className="account-avatar">{extractAccount(acc.url).charAt(0)}</span>
                      <span className="account-details">
                        <span className="account-name">{extractAccount(acc.url)}</span>
                        {acc.username && <span className="account-user">{acc.username}</span>}
                      </span>
                      {REGION_SHORT[acc.region] && (
                        <span
                          className={`account-region account-region--${acc.region}`}
                          title={`${REGION_NAMES[acc.region]} region${acc.regionUrl ? ' — selecting this takes you there' : ''}`}
                        >
                          {REGION_SHORT[acc.region]}
                        </span>
                      )}
                      {active && <span className="account-check">✓</span>}
                      <button
                        type="button"
                        className="account-delete"
                        title="Remove this account"
                        onClick={(e) => handleDeleteAccount(acc, e)}
                        disabled={loading}
                      >
                        ×
                      </button>
                    </li>
                  );
                })}
              </ul>
              <button
                type="button"
                className="add-account-btn"
                onClick={handleAddAccount}
                disabled={loading}
              >
                + Add another account
              </button>
            </div>
          )}

          {selectedKey && !urlOpen && (
            <button
              type="button"
              className="url-summary"
              onClick={() => setUrlOpen(true)}
              disabled={loading}
              title="Show, copy or edit the account URL"
              aria-expanded={false}
            >
              <span className="url-summary-label">Orcanos URL</span>
              <span className="url-summary-value">{orcanos_url}</span>
              <span className="url-summary-edit">Edit</span>
            </button>
          )}

          {(!selectedKey || urlOpen) && (
            <div className="form-group">
              <label htmlFor="orcanos_url">Orcanos URL:</label>
              <div className="url-field">
                <input
                  id="orcanos_url"
                  type="text"
                  value={orcanos_url}
                  onChange={(e) => handleUrlChange(e.target.value)}
                  onBlur={(e) => checkAccount(e.target.value)}
                  placeholder={DEFAULT_URL}
                  disabled={loading}
                  aria-invalid={gateBlocked}
                  autoFocus={urlOpen}
                />
                <button
                  type="button"
                  className="url-copy"
                  onClick={copyUrl}
                  disabled={loading || !orcanos_url}
                  title="Copy the URL"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <ul className="url-hint">
                <li>Global cloud: <code>https://app.orcanos.com/your-account</code></li>
                <li>US cloud: <code>https://us.orcanos.com/your-account</code></li>
                <li>On-premise: <code>https://your-server/your-account</code></li>
              </ul>
            </div>
          )}

          {/*
            Wrong region. Deliberately NOT the gate message: the account is fine and the user
            is not blocked, they are simply at the wrong address. Offering "contact us" here
            would read as a refusal for something they can fix in one click.
          */}
          {handingOff && (
            <div className="gate-message gate-message--region" role="status">
              <strong>Taking you to your region…</strong>
              <p>This account&rsquo;s data is stored in another region. One moment.</p>
            </div>
          )}

          {wrongRegion && !handingOff && (
            <div className="gate-message gate-message--region" role="alert">
              <strong>This account is hosted in another region.</strong>
              <p>
                {gate.region === 'eu'
                  ? 'Its data is stored in the European Union, so it cannot be reached from this address.'
                  : 'Its data is stored in a different region, so it cannot be reached from this address.'}
              </p>
              {gate.regionUrl ? (
                <a className="btn-region-go" href={gate.regionUrl}>
                  Continue at {gate.regionUrl.replace(/^https?:\/\//, '')}
                </a>
              ) : (
                <p>
                  <GateMessage
                    text="The address for that region is not configured here — please contact us for it."
                    email={gate.email}
                  />
                </p>
              )}
            </div>
          )}

          {awaitingAccount && !gateBlocked && !wrongRegion && !handingOff && (
            <div className="gate-message gate-message--hint" role="status">
              Replace <code>{ACCOUNT_PLACEHOLDER}</code> in the URL above with your Orcanos
              account name to continue.
            </div>
          )}

          {gateBlocked && !wrongRegion && (
            <div className="gate-message" role="alert">
              <GateMessage text={gate.message} email={gate.email} />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username">Username:</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              disabled={loading || !credsEnabled}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password:</label>
            <div className="pwd-field">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                disabled={loading || !credsEnabled}
                required
                autoFocus={!!selectedKey}
              />
              <button
                type="button"
                className="pwd-toggle"
                onClick={() => setShowPassword((v) => !v)}
                disabled={loading || !credsEnabled}
                title={showPassword ? 'Hide password' : 'Show password'}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
              >
                <EyeIcon off={showPassword} />
              </button>
            </div>
            {forgotUrl && (
              <a
                className="forgot-password-link"
                href={forgotUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Forgot password?
              </a>
            )}
          </div>

          {/* Why the user is back here — set only by the automatic sign-out. A real login
              error supersedes it, so the two never stack. */}
          {notice && !error && <div className="login-notice">{notice}</div>}
          {error && <div className="error-message">{error}</div>}

          <button type="submit" disabled={loading || !credsEnabled} className="login-button">
            {loading ? 'Signing in...' : gate.status === 'checking' ? 'Checking account...' : 'Sign In'}
          </button>
        </form>
        <RegionNote region={gate.selfRegion} />
        {version && <p className="login-version">v{version}</p>}
      </div>
      <AppFooter version={version} variant="login" account={awaitingAccount ? '' : extractAccount(orcanos_url)} />
    </div>
  );
}

export default LoginPage;
